import type {
  ImmutableProgram,
  InitialProperties,
  ImmutableResult,
  UpdateHandler,
  Cmd,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {VariantsFor} from '@gl/lit/types/architecture.js';
import {html} from 'lit';
import {ifDefined} from 'lit/directives/if-defined.js';
import type AudienceEntityCard from './index.js';
import {
  buildHref,
  filterDatasetGroups,
  isTheMostFrequentlyUsedDataset,
} from './utils.js';
import {BadgeVariants} from '@gl/lit/components/base/badge/index.js';
import {when} from 'lit/directives/when.js';
import {pluralize} from '@gl/lit/utils/strings.js';
import type {DatasetGroup} from '@gl/lit/types/datasets.js';
import {colorVariant} from '@gl/lit/components/base/avatar/utils.js';
import {repeat} from 'lit/directives/repeat.js';
import {classMap} from 'lit/directives/class-map.js';
import {debounce} from '@gl/utils/debounce.js';
import {locale} from '@gl/lit/common/locale/index.js';
import type DatasetSelectionOptions from './index.js';
import type {CustomInputEvent} from '@gl/lit/components/base/input/index.js';
import {Variant as InputVariant} from '@gl/lit/components/base/input/index.js';
import {DEBOUNCE_INPUT_TIME_MS} from '@gl/lit/common/constants/debounce_times.js';
import {datasetSelectionOptions} from '@gl/lit/common/locale/dataset_selection_options.js';

const componentLocale = datasetSelectionOptions[locale.current];

export type Model = {
  filterTerm: string;
  datasetGroups: DatasetGroup[];
  filteredDatasetGroups: DatasetGroup[];
  isModalOpen: boolean;
  href: string;
  isAuthorizedForSourceConnections: boolean;
};

export const Msg = makeVariants({
  setDatasetGroups: Variant<DatasetGroup[]>()('set-dataset-groups'),
  setFilterTerm: Variant<string>()('set-filter-term'),
  setFilteredDatasetGroups: Variant<DatasetGroup[]>()(
    'set-filtered-dataset-groups'
  ),
  setIsModalOpen: Variant<boolean>()('set-is-modal-open'),
  setHref: Variant<string>()('set-href'),
  setIsAuthorizedForSourceConnections: Variant<boolean>()(
    'set-is-authorized-for-source-connections'
  ),
});

export type Msg = VariantsFor<typeof Msg>;

const debouncedFilterDatasetGroups = debounce(
  (
    datasetGroups: DatasetGroup[],
    filterTem: string,
    callback: (list: DatasetGroup[]) => unknown
  ) => {
    const result = filterDatasetGroups(datasetGroups, filterTem);
    callback(result);
  },
  DEBOUNCE_INPUT_TIME_MS
);

// -- Init

function init(props: InitialProperties): ImmutableResult<Model> {
  const datasetGroups =
    <DatasetGroup[] | undefined>props.get('datasetGroups') || [];

  return {
    datasetGroups,
    filterTerm: '',
    filteredDatasetGroups: datasetGroups,
    isModalOpen: false,
    href: <string>props.get('href') || '',
    isAuthorizedForSourceConnections:
      <boolean>props.get('isAuthorizedForSourceConnections') ?? false,
  };
}

// -- Update

function update(msg: Msg, model: Model, cmd: Cmd): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'set-dataset-groups': {
      return {
        ...model,
        datasetGroups: msg.value,
      };
    }
    case 'set-is-modal-open': {
      return {
        ...model,
        isModalOpen: msg.value,
      };
    }
    case 'set-is-authorized-for-source-connections': {
      return {
        ...model,
        isAuthorizedForSourceConnections: msg.value,
      };
    }
    case 'set-filter-term': {
      const filterTerm = msg.value;

      return [
        {
          ...model,
          filterTerm: msg.value,
        },
        cmd((host: DatasetSelectionOptions) => {
          debouncedFilterDatasetGroups(
            host.model.datasetGroups,
            filterTerm,
            result => {
              host.handleUpdate(Msg.setFilteredDatasetGroups(result));
            }
          );
        }),
      ];
    }
    case 'set-filtered-dataset-groups': {
      return {
        ...model,
        filteredDatasetGroups: msg.value,
      };
    }
    case 'set-href': {
      return {
        ...model,
        href: msg.value,
      };
    }
  }
}

function renderDisabledLink(isAuthorizedForSourceConnections: boolean) {
  if (isAuthorizedForSourceConnections) {
    return html`<div>
      <div class="selection__option--disabled-info">
        To begin using the audience studio for the dataset group above, you must
        first enable marve approval.<br />
        Please do so by clicking <a href="/source_connections"> here.</a>
      </div>
    </div>`;
  } else {
    return html`<div>
      <div class="selection__option--disabled-info">
        Please contact your admin to enable Marve for this dataset group.
      </div>
    </div>`;
  }
}

function renderDatasetGroup(
  datasetGroup: DatasetGroup,
  idx: number,
  model: Model
) {
  const {href, isAuthorizedForSourceConnections} = model;
  const isDisabled =
    !datasetGroup.marve_approval && href === '/audience_studio';

  const fullHref = isDisabled ? null : buildHref(datasetGroup.id) + href;

  return html`
    <div class="selection__option--container">
      <a
        class="audience-entity-card--modal--link "
        href="${ifDefined(fullHref ?? undefined)}"
      >
        <div
          class="selection__option ${classMap({
            'selection__option--disabled': isDisabled,
          })}"
        >
          <div class="selection__option__avatar">
            <fly-avatar
              name="${datasetGroup.name}"
              variant="${colorVariant(datasetGroup.name)}"
              circle
              size="40"
            ></fly-avatar>
          </div>
          <div class="selection__option__info">
            <div class="selection__option__info--header">
              ${datasetGroup.name}
            </div>
            <div class="selection__option__info--sub-header">
              ${datasetGroup.description}
            </div>
            <div class="selection__option__info__metadata">
              <div class="selection__option__info__metadata--customer-details">
                <!-- FIXME(CHarlie) - hack to prevent tooltip placement bug for stuidio link click -->
                <fly-tooltip
                  ?disabled=${href === '/audience_studio'}
                  content="Primary table connected for this dataset group"
                  ><fly-badge variant="primary--light"
                    >${datasetGroup.primary_dataset_name}</fly-badge
                  ></fly-tooltip
                >
              </div>
              <div>
                <small
                  >${pluralize(
                    datasetGroup.audiences_count ?? 0,
                    'Audience'
                  )}</small
                >
              </div>
              <div>
                <small
                  >${pluralize(
                    datasetGroup.dataset_count ?? 0,
                    'Dataset'
                  )}</small
                >
              </div>
            </div>
          </div>
          ${when(
            isTheMostFrequentlyUsedDataset(idx, model),
            () => html`
              <fly-badge
                variant=${BadgeVariants.YELLOW}
                class="selection__option__metabadge"
                >Most Popular</fly-badge
              >
            `
          )}
        </div>
      </a>
      ${when(isDisabled, () =>
        renderDisabledLink(isAuthorizedForSourceConnections)
      )}
    </div>
  `;
}

function view(
  this: AudienceEntityCard,
  model: Model,
  handleUpdate: UpdateHandler<Model, Msg>
) {
  return html`
    <div class="audience-entity-card--modal--container">
      <fly-input
        hasicon
        type="search"
        placeholder="Search"
        style="display: block;--fly-input-width: 100%;"
        variant=${InputVariant.BORDERLESS_NO_BG}
        @input=${(event: CustomEvent<CustomInputEvent>) => {
          const value = event.detail.value;
          handleUpdate(Msg.setFilterTerm(value));
        }}
      >
        <div slot="icon">
          <fly-icon icon="search"></fly-icon>
        </div>
      </fly-input>
      ${repeat(
        model.filteredDatasetGroups,
        datasetGroup => datasetGroup.id,
        (datasetGroup, idx) => renderDatasetGroup(datasetGroup, idx, model)
      )}
      ${when(
        model.filteredDatasetGroups.length === 0,
        () => html`${componentLocale.noDatasetGroupsFound}`
      )}
    </div>
  `;
}

// -- Program

export var main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view,
};
